import api from "@/axiosInterceptor";

const DASHBOARD_URL = "public/api/samples/qc/dashboard";

export function fetchQaqcDashboard() {
  return api.get(DASHBOARD_URL).then((res) => {
    if (res && res.data && (res.data.statusCode === 200 || res.data.statusCode === 201)) {
      return res.data.data;
    }
    throw new Error(res?.data?.message || "Failed to fetch QA/QC dashboard");
  });
}

const NA = "-";

function fmtCount(v) {
  if (v === undefined || v === null || v === "") return NA;
  const n = Number(v);
  return Number.isNaN(n) ? NA : n;
}

function fmtPct(total, part) {
  if (total === undefined || total === null || total === "" || Number(total) === 0) return NA;
  const t = Number(total);
  const p = Number(part);
  if (Number.isNaN(t) || Number.isNaN(p)) return NA;
  return `${Math.round((p / t) * 100)}%`;
}

function pct(total, part) {
  if (!total || total === 0) return "0%";
  return `${Math.round((Number(part) / Number(total)) * 100)}%`;
}

function performance(passRatePct) {
  if (passRatePct >= 90) return "Good";
  if (passRatePct >= 70) return "Fair";
  return "Poor";
}

export function mapDashboardToOverview(apiData) {
  if (!apiData) return null;
  const cce = apiData.CCETests || {};
  const cceCounts = cce.Counts || {};
  const cceGas = apiData.CCETestsGas || {};
  const cceGasCounts = cceGas.Counts || {};
  const dl = apiData.DLTests || {};
  const dlCounts = dl.Counts || {};

  const cceTotal = Number(cceCounts.Total) || 0;
  const dlTotal = Number(dlCounts.Total) || 0;
  const totalTestsSum = cceTotal + dlTotal;

  const ccePassed = Number(cceCounts.Passed) || 0;
  const dlPassed = Number(dlCounts.Passed) || 0;
  const passedSum = ccePassed + dlPassed;

  const cceFailed = Number(cceCounts.Failed) || 0;
  const dlFailed = Number(dlCounts.Failed) || 0;
  const failedSum = cceFailed + dlFailed;

  const ccePending = Number(cceCounts.Pending) || 0;
  const dlPending = Number(dlCounts.Pending) || 0;
  const pendingSum = ccePending + dlPending;

  const summaryStats = {
    totalTests: fmtCount(totalTestsSum),
    totalTestsChange: "",
    cceTests: fmtCount(cceCounts.Total),
    cceTestsPercent: totalTestsSum ? pct(totalTestsSum, cceCounts.Total) + " of total" : NA,
    dlTests: fmtCount(dlCounts.Total),
    dlTestsPercent: totalTestsSum ? pct(totalTestsSum, dlCounts.Total) + " of total" : NA,
    passRate: fmtPct(totalTestsSum, passedSum),
  };

  const qcStatusOverview = {
    categories: ["Passed", "Failed", "Needs Review"],
    series: [
      { name: "CCE Tests", color: "#2298D5", values: [cceCounts.Passed || 0, cceCounts.Failed || 0, cceCounts.Pending || 0] },
      { name: "DL Tests", color: "#F97316", values: [dlCounts.Passed || 0, dlCounts.Failed || 0, dlCounts.Pending || 0] },
    ],
  };

  const qcStatusPanel = {
    passed: { count: fmtCount(passedSum), total: fmtCount(totalTestsSum), percent: fmtPct(totalTestsSum, passedSum) },
    needsReview: { count: fmtCount(pendingSum), total: fmtCount(totalTestsSum), percent: fmtPct(totalTestsSum, pendingSum) },
    failed: { count: fmtCount(failedSum), total: fmtCount(totalTestsSum), percent: fmtPct(totalTestsSum, failedSum) },
    subtitle: "Current QC validation results",
  };

  function wellsToScorecardRows(wells) {
    if (!Array.isArray(wells)) return [];
    return wells.map((w) => {
      const c = w.Counts || {};
      const total = Number(c.Total) || 0;
      const passed = Number(c.Passed) || 0;
      const qc = w.QualityCounts || {};
      const qcTotal = Number(qc.Total) || 0;
      const qcPassed = Number(qc.Passed) || 0;
      return {
        well: w.Name || NA,
        samples: fmtCount(c.Total),
        passRate: fmtCount(qc.PassRate),
        avgQualityScore: fmtCount(qc.AvgQualityScore),
        passFailPerformance: NA,
      };
    });
  }

  const DL_CONSISTENCY_CROSS_ORDER = ["saturationPressure_Consistent", "oilDensity_Consistent"];
  const DL_CONSISTENCY_ORDERING_ORDER = [
    "solutionGOR_Hierarchy",
    "oilFormationVolumeFactor_Hierarchy",
    "residualOilDensity_Hierarchy",
  ];
  const DL_EQUILIBRIUM_ORDER = ["Hofman_Linear", "Wilson_Linear", "Buckley_Linear_Negative"];
  const DL_VALIDATION_PASS_RATE_MATRIX_ORDER = [
    ...DL_CONSISTENCY_ORDERING_ORDER,
    "GOR_Increases",
    "oilFormationVolumeFactor_Increases",
    "oilDensity_Increases",
    "gasGravity_Increases",
    "gasFormationVolumeFactor_Increases",
    "gasZFactor__aprroaches_unity",
  ];

  function matrixItemToRow(m) {
    const c = m.Counts || {};
    const total = Number(c.Total) || 0;
    const passed = Number(c.Passed) || 0;
    const failedSamples = Array.isArray(m.FailedSamples)
      ? m.FailedSamples.map((fs) => ({
          fluidSampleID: fs.FluidSampleID != null ? String(fs.FluidSampleID) : "",
          id: fs.ID != null ? String(fs.ID) : "",
        })).filter((fs) => fs.id)
      : [];
    return {
      matrixName: m.Name || "",
      check: m.DisplayName || m.Name || NA,
      passed: fmtCount(c.Passed),
      failed: fmtCount(c.Failed),
      passRate: fmtPct(total, passed),
      failedSamples,
    };
  }

  function matrixToRows(matrix) {
    if (!Array.isArray(matrix)) return [];
    return matrix.map(matrixItemToRow);
  }

  function matrixItemToDlConsistencyStyleRow(m) {
    const c = m.Counts || {};
    const total = Number(c.Total) || 0;
    const passed = Number(c.Passed) || 0;
    const failedSamples = Array.isArray(m.FailedSamples)
      ? m.FailedSamples.map((fs) => ({
          fluidSampleID: fs.FluidSampleID != null ? String(fs.FluidSampleID) : "",
          id: fs.ID != null ? String(fs.ID) : "",
        })).filter((fs) => fs.id || fs.fluidSampleID)
      : [];
    return {
      displayName: m.DisplayName || m.Name || NA,
      field: m.Name || "",
      compliant: fmtCount(c.Passed),
      nonCompliant: fmtCount(c.Failed),
      complianceRate: fmtPct(total, passed),
      fluidSampleIds: [],
      failedSamples,
    };
  }

  function orderMatrixItemsByName(items, nameOrder) {
    const map = new Map(items.map((x) => [x.Name, x]));
    return nameOrder.map((n) => map.get(n)).filter(Boolean);
  }

  function splitDlValidationMatrix(matrix) {
    const cross = [];
    const ordering = [];
    const equilibrium = [];
    const reserved = new Set([
      ...DL_CONSISTENCY_CROSS_ORDER,
      ...DL_CONSISTENCY_ORDERING_ORDER,
      ...DL_EQUILIBRIUM_ORDER,
    ]);
    if (!Array.isArray(matrix)) {
      return {
        validation: [],
        cross,
        ordering,
        equilibrium,
      };
    }
    for (const m of matrix) {
      const name = m.Name || "";
      if (DL_CONSISTENCY_CROSS_ORDER.includes(name)) cross.push(m);
      else if (DL_CONSISTENCY_ORDERING_ORDER.includes(name)) ordering.push(m);
      else if (DL_EQUILIBRIUM_ORDER.includes(name)) equilibrium.push(m);
    }
    const byName = new Map(matrix.map((m) => [m.Name, m]));
    const validationHead = DL_VALIDATION_PASS_RATE_MATRIX_ORDER.map((n) => byName.get(n)).filter(Boolean);
    const headNames = new Set(DL_VALIDATION_PASS_RATE_MATRIX_ORDER);
    const validationTail = [];
    for (const m of matrix) {
      const name = m.Name || "";
      if (reserved.has(name) || headNames.has(name)) continue;
      validationTail.push(m);
    }
    return {
      validation: [...validationHead, ...validationTail],
      cross: orderMatrixItemsByName(cross, DL_CONSISTENCY_CROSS_ORDER),
      ordering: orderMatrixItemsByName(ordering, DL_CONSISTENCY_ORDERING_ORDER),
      equilibrium: orderMatrixItemsByName(equilibrium, DL_EQUILIBRIUM_ORDER),
    };
  }

  function summaryFromCounts(counts) {
    const c = counts || {};
    const total = Number(c.Total) || 0;
    const passed = Number(c.Passed) || 0;
    const failed = Number(c.Failed) || 0;
    const pending = Number(c.Pending) || 0;
    const den = passed + failed + pending || total;
    return {
      overallPassRate: fmtPct(den, passed),
      passedQc: fmtCount(c.Passed),
      pendingQc: fmtCount(c.Pending),
      failed: fmtCount(c.Failed),
    };
  }

  function distPlotsToOptions(plots) {
    if (!Array.isArray(plots)) return [];
    return plots.map((p) => ({
      id: p.Name || p.DisplayName || "",
      label: p.DisplayName || p.Name || "",
    }));
  }

  function distPlotToData(plot, selectedId) {
    if (!plot || plot.Name !== selectedId) return null;
    const keys = plot.DistributionPlotKeys || [];
    const categories = keys.map((k) => k.Key);
    const values = keys.map((k) => Number(k.Value) || 0);
    const total = values.reduce((a, b) => a + b, 0);
    const validKeys = keys.filter((k) => k.Key !== "N/A" && k.Key !== "Failed" && k.Key !== "Not Reported");
    const validSum = validKeys.reduce((a, k) => a + (Number(k.Value) || 0), 0);
    const outsideSum = total - validSum;
    return {
      categories,
      validRange: {
        values,
        label: "Valid Range",
        count: total === 0 ? NA : validSum,
        percent: total ? pct(total, validSum) : NA,
      },
      outsideRange: {
        count: total === 0 ? NA : outsideSum,
        label: total === 0 ? "Outside Range - not available" : (outsideSum ? `Outside Range (${outsideSum} samples) - Click to view` : "Outside Range - Click to view"),
      },
    };
  }

  const sampleIds = [];
  const consistencyCheck = cce.ConsistencyCheck || [];
  consistencyCheck.forEach((item) => {
    (item.FluidSampleIDs || []).forEach((id) => {
      if (id && !sampleIds.includes(String(id))) sampleIds.push(String(id));
    });
  });
  const dlConsistency = dl.ConsistencyCheck || [];
  dlConsistency.forEach((item) => {
    (item.FluidSampleIDs || []).forEach((id) => {
      if (id && !sampleIds.includes(String(id))) sampleIds.push(String(id));
    });
  });
  (dl.ValidationPassRateMatrix || []).forEach((m) => {
    (m.FailedSamples || []).forEach((fs) => {
      const fid = fs.FluidSampleID != null ? String(fs.FluidSampleID) : "";
      if (fid && !sampleIds.includes(fid)) sampleIds.push(fid);
    });
  });

  return {
    summaryStats,
    qcStatusOverview,
    qcStatusPanel,
    byTab: {
      oilCce: {
        scorecardRows: wellsToScorecardRows(cce.Wells),
        scorecardSummary: summaryFromCounts(cceCounts),
        validationMatrixRows: matrixToRows(cce.ValidationPassRateMatrix),
        validationMatrixTitle: "Validation Pass Rate Matrix",
        validationMatrixDescription: "Percentage of all CCE (Oil) samples passing each validation check",
        distributionPlotOptions: distPlotsToOptions(cce.DistributionPlots),
        distributionPlots: cce.DistributionPlots || [],
      },
      gasCce: {
        scorecardRows: wellsToScorecardRows(cceGas.Wells),
        scorecardSummary: summaryFromCounts(cceGasCounts),
        validationMatrixRows: matrixToRows(cceGas.ValidationPassRateMatrix),
        validationMatrixTitle: "Validation Pass Rate Matrix",
        validationMatrixDescription: "Percentage of all CCE (Gas) samples passing each validation check",
        distributionPlotOptions: distPlotsToOptions(cceGas.DistributionPlots),
        distributionPlots: cceGas.DistributionPlots || [],
      },
      dlTests: (() => {
        const split = splitDlValidationMatrix(dl.ValidationPassRateMatrix);
        return {
          scorecardRows: wellsToScorecardRows(dl.Wells),
          scorecardSummary: summaryFromCounts(dlCounts),
          validationMatrixRows: split.validation.map(matrixItemToRow),
          validationMatrixTitle: "Validation Pass Rate Matrix",
          validationMatrixDescription: "Percentage of all DL samples passing each validation check",
          distributionPlotOptions: distPlotsToOptions(dl.DistributionPlots),
          distributionPlots: dl.DistributionPlots || [],
          consistencyCrossTestRows: split.cross.map(matrixItemToDlConsistencyStyleRow),
          consistencyOrderingRows: split.ordering.map(matrixItemToDlConsistencyStyleRow),
          equilibriumPlotRows: split.equilibrium.map(matrixItemToDlConsistencyStyleRow),
        };
      })(),
    },
    sampleIds,
  };
}
